import { ArrowUpRight } from "lucide-react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";

const SOLUTIONS = [
  {
    slug: "sistemas",
    title: "Sistemas sob medida",
    statement: "Sua operação inteira, conectada em um sistema que nasce do seu jeito.",
    description: "Produtos digitais construídos ao redor da sua operação, sem obrigar sua empresa a se adaptar a uma ferramenta genérica.",
    tags: ["Produto digital", "Painéis", "Integrações"],
    theme: "system",
    image: "/media/solutions/system-concept.png",
    imageAlt: "Ecossistema visual de uma operação integrada em um sistema sob medida.",
  },
  {
    slug: "crm",
    title: "CRM e vendas",
    statement: "Cada contato no lugar certo. Cada oportunidade com um próximo passo.",
    description: "Organização de contatos, atendimentos e oportunidades em um fluxo comercial que a equipe consegue acompanhar.",
    tags: ["Pipeline", "Atendimento", "Gestão"],
    theme: "crm",
    image: "/media/solutions/crm-concept.png",
    imageAlt: "Visão conceitual de um CRM conectado ao funil, aos contatos e aos indicadores.",
  },
  {
    slug: "automacao",
    title: "Automação e IA",
    statement: "Menos trabalho repetido. Mais decisões acontecendo no tempo certo.",
    description: "Conversas, tarefas e rotinas conectadas para reduzir trabalho manual e acelerar respostas.",
    tags: ["WhatsApp", "Agentes de IA", "Fluxos"],
    theme: "automation",
    image: "/media/solutions/automation-concept.png",
    imageAlt: "Núcleo conceitual de automação conectando mensagens, dados, decisões e resultados.",
  },
  {
    slug: "web",
    title: "Sites e experiências web",
    statement: "Sua presença digital precisa parecer tão boa quanto o seu negócio.",
    description: "Presença digital com estratégia, performance e uma experiência visual à altura do seu negócio.",
    tags: ["Sites", "Landing pages", "Interfaces"],
    theme: "web",
    image: "/media/solutions/web-concept.png",
    imageAlt: "Experiência web responsiva apresentada em desktop, tablet e celular.",
  },
];

const SolutionVisual = ({ solution }) => (
  <figure className="group relative h-full w-full overflow-hidden">
    <img
      src={solution.image}
      alt={solution.imageAlt}
      loading="lazy"
      decoding="async"
      className="h-full w-full object-cover object-center transition-transform duration-700 ease-out group-hover:scale-[1.012]"
    />
  </figure>
);

const THEME_CLASSES = {
  system: "bg-[#f1f3ec] text-[#050606]",
  crm: "bg-[#09262a] text-white",
  automation: "bg-lime-300 text-[#050606]",
  web: "bg-[#f2edf8] text-[#1b1029]",
};

const SolutionSlide = ({ solution }) => (
  <article className="h-full w-screen shrink-0 px-4 sm:px-6 lg:px-8">
    <div className={`mx-auto grid h-full max-w-[1440px] grid-rows-[auto_1fr] overflow-hidden lg:grid-cols-[.72fr_1.28fr] lg:grid-rows-1 ${THEME_CLASSES[solution.theme]}`}>
      <div className="flex flex-col justify-between p-5 sm:p-8 lg:p-12">
        <div>
          <h3 className="display-title max-w-2xl text-[clamp(2.4rem,4vw,4.75rem)] leading-[.92]">{solution.title}</h3>
          <p className="mt-5 max-w-xl text-lg font-semibold leading-tight sm:text-xl lg:text-2xl">{solution.statement}</p>
          <p className={`mt-4 hidden max-w-xl text-sm leading-relaxed sm:block sm:text-base ${solution.theme === "crm" ? "text-white/58" : "opacity-60"}`}>{solution.description}</p>
        </div>
        <div className="mt-8 flex flex-wrap items-center gap-2 border-t border-current/15 pt-5">
          {solution.tags.map((tag) => <span key={tag} className="border border-current/18 px-3 py-2 text-[8px] font-bold uppercase tracking-[.13em]">{tag}</span>)}
          <Link to="/servicos" className="group ml-auto flex h-10 w-10 items-center justify-center rounded-full border border-current/25 transition-transform hover:scale-105" aria-label={`Conhecer ${solution.title}`}><ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></Link>
        </div>
      </div>
      <div className="min-h-0 overflow-hidden">
        <SolutionVisual solution={solution} />
      </div>
    </div>
  </article>
);

const StaticSolutions = () => (
  <section id="solucoes-em-movimento" data-header-theme="dark" className="bg-[#050606] py-24">
    <div className="site-shell">
      <p className="eyebrow">Soluções que nascem do negócio</p>
    </div>
    <div className="mt-10 space-y-6">
      {SOLUTIONS.map((solution) => <div key={solution.slug} className="h-[720px]"><SolutionSlide solution={solution} /></div>)}
    </div>
  </section>
);

export const ScrollStory = () => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end end"],
  });
  const trackX = useTransform(scrollYProgress, [0, 1], ["0vw", "-300vw"]);
  const progressScale = useTransform(scrollYProgress, [0, 1], [0, 1]);

  if (reduce) return <StaticSolutions />;

  return (
    <section ref={ref} id="solucoes-em-movimento" data-header-theme="dark" className="relative h-[325svh] bg-[#050606]" data-testid="scroll-story">
      <div className="sticky top-0 h-[100svh] min-h-[650px] overflow-hidden bg-[#050606] pb-14 pt-[96px]">
        <div className="site-shell mb-5 flex items-center justify-between gap-6">
          <p className="eyebrow">Soluções que nascem do negócio</p>
          <p className="hidden max-w-md text-right text-xs leading-relaxed text-white/38 md:block">Cada desafio pede uma resposta diferente. A estrutura certa conecta todas elas.</p>
        </div>

        <div className="h-[calc(100%_-_70px)] overflow-hidden">
          <motion.div className="flex h-full w-[400vw]" style={{ x: trackX }}>
            {SOLUTIONS.map((solution) => <SolutionSlide key={solution.slug} solution={solution} />)}
          </motion.div>
        </div>

        <div className="site-shell absolute inset-x-0 bottom-5">
          <div className="mb-2 hidden grid-cols-4 text-[8px] font-bold uppercase tracking-[.14em] text-white/30 sm:grid">
            {SOLUTIONS.map((solution) => <span key={solution.slug}>{solution.title}</span>)}
          </div>
          <div className="h-[3px] overflow-hidden bg-white/10"><motion.div className="h-full origin-left bg-lime-300" style={{ scaleX: progressScale }} /></div>
        </div>
      </div>
    </section>
  );
};
